import { FormEvent, useMemo, useState } from 'react'
import { ClinicalInput, UrineKetones } from './clinical/engine'
import { Episode } from './clinical/episode'
import { correctEpisodeObservation, getObservationCorrections } from './clinical/corrections'
import { RuleReferences } from './ReferenceLinks'
import { saveEpisode } from './storage'

const num = (value: FormDataEntryValue | null): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : undefined
}

const urineKetonesValue = (value: FormDataEntryValue | null): UrineKetones | undefined => {
  const parsed = num(value)
  return parsed === 0 || parsed === 0.5 || parsed === 1 || parsed === 2 || parsed === 3 || parsed === 4 ? parsed : undefined
}

const boolChoice = (value: FormDataEntryValue | null): boolean | undefined => value === 'yes' ? true : value === 'no' ? false : undefined
const value = (item: number | string | undefined) => item ?? ''
const toLocalInput = (iso: string) => {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const dateTimeLabel = (iso: string) => new Intl.DateTimeFormat('it-IT', {
  day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit'
}).format(new Date(iso))
const ketoneLabel = (input: ClinicalInput) => input.betaHydroxybutyrateMmolL !== undefined
  ? `β-OHB ${input.betaHydroxybutyrateMmolL}`
  : input.urineKetones !== undefined
    ? input.urineKetones === 0 ? 'chetonuria neg' : input.urineKetones === 0.5 ? 'chetonuria tracce' : `chetonuria ${input.urineKetones}+`
    : 'chetoni —'

export default function ObservationCorrectionPanel({ episode, onChange }: { episode: Episode; onChange: (episode: Episode) => void }) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const editing = useMemo(() => episode.observations.find((item) => item.id === editingId), [episode.observations, editingId])
  const invalidatesWorkflow = Boolean(episode.resolutionConfirmation || episode.transitionPlan || episode.status === 'resolved')

  const saveCorrection = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!editing) return
    const fd = new FormData(event.currentTarget)
    const collectedRaw = String(fd.get('collectedAt') || '')
    if (!collectedRaw) return
    const ph = num(fd.get('ph'))

    const input: ClinicalInput = {
      ...editing.input,
      glucoseMgDl: num(fd.get('glucoseMgDl')),
      sodiumMmolL: num(fd.get('sodiumMmolL')),
      potassiumMmolL: num(fd.get('potassiumMmolL')),
      chlorideMmolL: num(fd.get('chlorideMmolL')),
      bicarbonateMmolL: num(fd.get('bicarbonateMmolL')),
      ph,
      bloodGasSample: ph !== undefined ? ((fd.get('bloodGasSample') as ClinicalInput['bloodGasSample']) || 'venous') : undefined,
      betaHydroxybutyrateMmolL: num(fd.get('betaHydroxybutyrateMmolL')),
      urineKetones: urineKetonesValue(fd.get('urineKetones')),
      ureaMmolL: num(fd.get('ureaMmolL')),
      measuredOsmolality: num(fd.get('measuredOsmolality')),
      urineOutputMlKgH: num(fd.get('urineOutputMlKgH')),
      cognitionImproved: boolChoice(fd.get('cognitionImproved')),
      mentalStatus: (fd.get('mentalStatus') as ClinicalInput['mentalStatus']) || 'unknown'
    }

    const next = correctEpisodeObservation(episode, editing.id, {
      collectedAt: new Date(collectedRaw).toISOString(),
      input,
      reason: String(fd.get('correctionReason') || '').trim() || undefined
    })
    onChange(next)
    await saveEpisode(next)
    setEditingId(null)
    setSaved(true)
    window.setTimeout(() => setSaved(false), 2200)
  }

  if (episode.status === 'closed') return null

  return (
    <section className="panel observation-correction-panel">
      <div className="correction-heading">
        <div>
          <p className="step">Qualità del dato · v0.4.2</p>
          <h2>Correggi rilevazioni precedenti</h2>
          <p>Qualsiasi rilevazione può essere corretta. Il salvataggio ricalcola l’intero episodio; gli eventi terapeutici restano indipendenti.</p>
        </div>
        <span className="audit-badge">AUDIT LOCALE</span>
      </div>

      {invalidatesWorkflow && (
        <div className="correction-warning"><strong>Attenzione:</strong> una correzione invaliderà la conferma di risoluzione e l’eventuale piano di transizione, che dovranno essere rivalidati sui dati corretti.</div>
      )}

      <RuleReferences ids={['correction_audit']} />

      <div className="correction-list">
        {episode.observations.map((observation, index) => {
          const history = getObservationCorrections(episode, observation.id)
          return (
            <article className={`correction-row ${editingId === observation.id ? 'editing' : ''}`} key={observation.id}>
              <div className="correction-row-main">
                <div className="correction-time"><strong>{index === 0 ? 'T0' : `#${index + 1}`}</strong><span>{dateTimeLabel(observation.collectedAt)}</span></div>
                <div className="correction-vitals"><span>Glu <strong>{observation.input.glucoseMgDl ?? '—'}</strong></span><span>K <strong>{observation.input.potassiumMmolL ?? '—'}</strong></span><span>{ketoneLabel(observation.input)}</span><span>pH <strong>{observation.input.ph ?? '—'}</strong></span></div>
                <div className="correction-actions">
                  {history.length > 0 && <span className="corrected-badge">corretta ×{history.length}</span>}
                  <button type="button" className="secondary compact-action" onClick={() => setEditingId(editingId === observation.id ? null : observation.id)}>{editingId === observation.id ? 'Annulla' : 'Modifica'}</button>
                </div>
              </div>
              {history.length > 0 && (
                <details className="correction-history">
                  <summary>Storico correzioni</summary>
                  {history.slice().reverse().map((item) => (
                    <div className="audit-entry" key={item.id}>
                      <strong>{dateTimeLabel(item.correctedAt)}</strong>
                      <span>Prima: {dateTimeLabel(item.previousCollectedAt)} · Glu {item.previousInput.glucoseMgDl ?? '—'} · Na {item.previousInput.sodiumMmolL ?? '—'} · K {item.previousInput.potassiumMmolL ?? '—'} · {ketoneLabel(item.previousInput)} · pH {item.previousInput.ph ?? '—'} · HCO₃ {item.previousInput.bicarbonateMmolL ?? '—'}</span>
                      {item.reason && <small>{item.reason}</small>}
                    </div>
                  ))}
                </details>
              )}
            </article>
          )
        })}
      </div>

      {editing && (
        <form className="observation-editor" onSubmit={saveCorrection} key={editing.id}>
          <div className="editor-heading"><div><strong>Modifica rilevazione</strong><span>{dateTimeLabel(editing.collectedAt)}</span></div><small>Le terapie registrate non vengono modificate da questo form.</small></div>
          <div className="editor-grid">
            <label><span>Data/ora</span><input type="datetime-local" name="collectedAt" defaultValue={toLocalInput(editing.collectedAt)} required /></label>
            <label><span>Glicemia</span><div className="input-wrap"><input type="number" step="any" name="glucoseMgDl" defaultValue={value(editing.input.glucoseMgDl)} /><small>mg/dL</small></div></label>
            <label><span>Na</span><div className="input-wrap"><input type="number" step="any" name="sodiumMmolL" defaultValue={value(editing.input.sodiumMmolL)} /><small>mmol/L</small></div></label>
            <label><span>K</span><div className="input-wrap"><input type="number" step="any" name="potassiumMmolL" defaultValue={value(editing.input.potassiumMmolL)} /><small>mmol/L</small></div></label>
            <label><span>Cl</span><div className="input-wrap"><input type="number" step="any" name="chlorideMmolL" defaultValue={value(editing.input.chlorideMmolL)} /><small>mmol/L</small></div></label>
            <label><span>HCO₃⁻</span><div className="input-wrap"><input type="number" step="any" name="bicarbonateMmolL" defaultValue={value(editing.input.bicarbonateMmolL)} /><small>mmol/L</small></div></label>
            <label><span>pH</span><input type="number" step="0.01" name="ph" defaultValue={value(editing.input.ph)} /></label>
            <label><span>Campione pH</span><select name="bloodGasSample" defaultValue={editing.input.bloodGasSample ?? 'venous'}><option value="venous">Venoso</option><option value="arterial">Arterioso</option></select></label>
            <label><span>β-OHB</span><div className="input-wrap"><input type="number" step="any" name="betaHydroxybutyrateMmolL" defaultValue={value(editing.input.betaHydroxybutyrateMmolL)} /><small>mmol/L</small></div></label>
            <label><span>Chetonuria</span><select name="urineKetones" defaultValue={editing.input.urineKetones ?? ''}><option value="">Non disponibile</option><option value="0">Negativi</option><option value="0.5">Tracce</option><option value="1">1+</option><option value="2">2+</option><option value="3">3+</option><option value="4">4+</option></select></label>
            <label><span>Urea</span><div className="input-wrap"><input type="number" step="any" name="ureaMmolL" defaultValue={value(editing.input.ureaMmolL)} /><small>mmol/L</small></div></label>
            <label><span>Osm misurata</span><div className="input-wrap"><input type="number" step="any" name="measuredOsmolality" defaultValue={value(editing.input.measuredOsmolality)} /><small>mOsm/kg</small></div></label>
            <label><span>Diuresi</span><div className="input-wrap"><input type="number" step="any" name="urineOutputMlKgH" defaultValue={value(editing.input.urineOutputMlKgH)} /><small>mL/kg/h</small></div></label>
            <label><span>Miglioramento cognitivo</span><select name="cognitionImproved" defaultValue={editing.input.cognitionImproved === true ? 'yes' : editing.input.cognitionImproved === false ? 'no' : ''}><option value="">Non valutato</option><option value="yes">Sì</option><option value="no">No</option></select></label>
            <label><span>Stato neurologico</span><select name="mentalStatus" defaultValue={editing.input.mentalStatus ?? 'unknown'}><option value="unknown">Non specificato</option><option value="alert">Vigile</option><option value="drowsy">Sonnolento</option><option value="stupor">Stupor</option><option value="coma">Coma</option></select></label>
            <label className="editor-reason"><span>Motivo correzione <small>opzionale</small></span><input type="text" name="correctionReason" placeholder="es. referto definitivo / errore di trascrizione" /></label>
          </div>
          <div className="editor-actions"><button type="button" className="secondary" onClick={() => setEditingId(null)}>Annulla</button><button type="submit" className="primary">Salva e ricalcola episodio</button></div>
        </form>
      )}

      {saved && <p className="saved correction-saved">Correzione salvata. Diagnosi, trend, alert e stato dell’episodio sono stati ricalcolati.</p>}
    </section>
  )
}
